import React, { useState } from 'react';
import FormGroup from './FormGroup';
import Input from './Input';
import Select from './Select';
import Textarea from './Textarea';
import Toggle from './Toggle';
import { decryptField, isEncrypted } from '../../utils/fieldEncryption';
import { copyText } from '../../utils/clipboard';
import './SchemaForm.css';

function formatKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Heuristic: should this string field render as a textarea? */
const LONG_TEXT_KEYS = new Set([
  'content', 'description', 'significance', 'motivation',
  'evidence', 'notes', 'body', 'text', 'summary', 'bio',
]);
function isLongText(key, propDef) {
  if (LONG_TEXT_KEYS.has(key)) return true;
  if (propDef.maxLength != null && propDef.maxLength > 200) return true;
  return false;
}

/**
 * Convert a stored UTC ISO datetime string to the YYYY-MM-DDTHH:mm:ss form
 * that <input type="datetime-local"> expects, expressed in the user's local
 * timezone. Storage is canonical UTC (e.g. "2026-04-23T03:53:23Z"); the
 * widget is intentionally tz-naive and would render empty if fed the raw Z.
 */
function utcIsoToLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Build local YYYY-MM-DDTHH:mm:ss without UTC drift via toISOString.
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Inverse: take a local YYYY-MM-DDTHH:mm[:ss] from the datetime-local widget
 * and serialize as UTC ISO with `Z` suffix for storage.
 */
function localInputValueToUtcIso(local) {
  if (!local) return '';
  const d = new Date(local); // browser parses as local time
  if (isNaN(d.getTime())) return local; // pass through if unparseable
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z'); // drop millis for cleanliness
}

function renderViewValue(value) {
  if (value === null || value === undefined) return <span className="schema-form__muted">Not set</span>;
  if (typeof value === 'boolean') return <span className="schema-form__pill">{value ? 'Yes' : 'No'}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="schema-form__muted">None</span>;
    return (
      <div className="schema-form__pills">
        {value.map((item, i) => (
          <span key={i} className="schema-form__pill">
            {typeof item === 'object' && item !== null
              ? (item.username || item.name || item.label || item.title || item.user_id || JSON.stringify(item))
              : String(item)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') return <pre className="schema-form__json">{JSON.stringify(value, null, 2)}</pre>;
  return <span>{String(value)}</span>;
}

function EncryptedViewField({ value, fieldName, encKeys }) {
  const [revealed, setRevealed] = useState(false);
  const [decrypted, setDecrypted] = useState(null);
  const [decryptError, setDecryptError] = useState(false);

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(false);
      return;
    }
    if (encKeys && isEncrypted(value)) {
      try {
        const plain = await decryptField(value, fieldName, encKeys);
        setDecrypted(typeof plain === 'object' ? JSON.stringify(plain) : String(plain ?? ''));
        setDecryptError(false);
      } catch {
        setDecryptError(true);
        setDecrypted(null);
      }
    }
    setRevealed(true);
  };

  return (
    <span className="schema-form__encrypted">
      <span className="schema-form__encrypted-icon">🔒</span>
      {revealed
        ? decryptError
          ? <span className="schema-form__encrypted-mask">[decrypt failed]</span>
          : <span className="schema-form__encrypted-value">{decrypted ?? String(value ?? '')}</span>
        : <span className="schema-form__encrypted-mask">••••••••</span>
      }
      <button
        type="button"
        className="schema-form__encrypted-toggle"
        onClick={handleReveal}
      >
        {revealed ? 'Hide' : 'Reveal'}
      </button>
    </span>
  );
}

// Type → icon for the Notion-style property list.
function FieldIcon({ propDef = {}, encrypted }) {
  const svg = (children) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
  );
  if (encrypted) return svg(<><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>);
  if (propDef.enum) return svg(<><circle cx="12" cy="12" r="9" /><path d="M8 11l4 4 4-4" /></>);
  const t = propDef.type, f = propDef.format;
  if (t === 'boolean') return svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 12l3 3 5-6" /></>);
  if (t === 'array') return svg(<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />);
  if (t === 'object') return svg(<path d="M9 4c-2 0-3 1-3 3v1.5c0 1-1 1.5-2 1.5 1 0 2 .5 2 1.5V14c0 2 1 3 3 3M15 4c2 0 3 1 3 3v1.5c0 1 1 1.5 2 1.5-1 0-2 .5-2 1.5V14c0 2-1 3-3 3" />);
  if (t === 'number' || t === 'integer') return svg(<path d="M5 9h14M5 15h14M11 4l-2 16M17 4l-2 16" />);
  if (f === 'date' || f === 'date-time') return svg(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></>);
  if (f === 'time') return svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
  if (f === 'email') return svg(<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>);
  if (f === 'uri') return svg(<path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />);
  return svg(<path d="M4 6h16M4 12h16M4 18h10" />); // text
}

function HashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </svg>
  );
}

// System/identity rows (e.g. the node's UUID) — read-only, muted, click-to-copy.
// These are not schema-defined; they describe the node itself and sit pinned at
// the top of the property list, above the schema attributes.
export function SystemPropRow({ field }) {
  // '' | 'copied' | 'failed' — "Copied" only once the clipboard took it.
  const [copyState, setCopyState] = useState('');
  const copy = async () => {
    if (!field.value) return;
    setCopyState((await copyText(String(field.value))) ? 'copied' : 'failed');
    setTimeout(() => setCopyState(''), 1200);
  };
  return (
    <div className="prop-row prop-row--system">
      <div className="prop-key" title={field.label}>
        <HashIcon />
        <span className="prop-key__name">{field.label}</span>
      </div>
      <div className="prop-value">
        <button type="button" className="prop-system-value" onClick={copy} title="Click to copy">
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Could not copy' : (field.value || '—')}
        </button>
      </div>
    </div>
  );
}

// Object property value — shows compact JSON inline; click to expand into an
// editable JSON editor (parses back to an object on blur, falls back to string).
function ObjectPropValue({ value, onChange, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const display = (typeof value === 'object' && value !== null) ? JSON.stringify(value) : (value ?? '');

  if (editing) {
    const commit = () => {
      const t = draft.trim();
      if (t === '') onChange(null);
      else { try { onChange(JSON.parse(t)); } catch { onChange(t); } }
      setEditing(false);
    };
    const rows = Math.min(12, Math.max(3, (draft.match(/\n/g)?.length || 0) + 1));
    return (
      <textarea
        className="prop-input prop-input--json"
        autoFocus
        rows={rows}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }
  return (
    <span
      className={`prop-input prop-input--readonly ${disabled ? '' : 'prop-input--clickable'}`}
      title={display}
      onClick={disabled ? undefined : () => {
        setDraft((typeof value === 'object' && value !== null) ? JSON.stringify(value, null, 2) : (value ?? ''));
        setEditing(true);
      }}
    >
      {display !== '' ? display : <span className="prop-empty">Empty</span>}
    </span>
  );
}

// Encrypted property value — masked + reveal (view), with a ✎ to set a new value.
function EncryptedPropValue({ value, fieldName, encKeys, onChange, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (editing) {
    const commit = () => { if (draft !== '') onChange(draft); setEditing(false); setDraft(''); };
    return (
      <input
        className="prop-input"
        autoFocus
        type="text"
        value={draft}
        placeholder="New value…"
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(''); } }}
      />
    );
  }
  return (
    <span className="prop-encrypted">
      <EncryptedViewField value={value} fieldName={fieldName} encKeys={encKeys} />
      {!disabled && (
        <button type="button" className="prop-edit-btn" title="Set new value" onClick={() => { setDraft(''); setEditing(true); }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
        </button>
      )}
    </span>
  );
}

// The editable value cell for a property row (borderless, Notion-style).
function renderPropValue(key, propDef, value, isEncrypted, handleChange, isDisabled, encKeys) {
  if (isEncrypted) return <EncryptedPropValue value={value} fieldName={key} encKeys={encKeys} onChange={handleChange} disabled={isDisabled} />;
  if (propDef.enum) {
    return (
      <select className="prop-input prop-input--select" value={value ?? ''} disabled={isDisabled} onChange={e => handleChange(e.target.value)}>
        <option value="">Empty</option>
        {propDef.enum.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
    );
  }
  if (propDef.type === 'boolean') {
    return <input type="checkbox" className="prop-checkbox" checked={!!value} disabled={isDisabled} onChange={e => handleChange(e.target.checked)} />;
  }
  if (propDef.type === 'array') {
    const items = Array.isArray(value) ? value : [];
    const hasObjects = items.some(item => typeof item === 'object' && item !== null);
    const displayValue = hasObjects
      ? [...new Set(items.map(item => item.username || item.name || item.label || item.title || item.user_id || JSON.stringify(item)))].join(', ')
      : items.join(', ');
    return <input className="prop-input" value={displayValue} placeholder="Empty" disabled={isDisabled || hasObjects}
      onChange={hasObjects ? undefined : (e => handleChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean)))} />;
  }
  if (propDef.type === 'object') {
    return <ObjectPropValue value={value} onChange={handleChange} disabled={isDisabled} />;
  }
  if (propDef.type === 'number' || propDef.type === 'integer') {
    return <input type="number" className="prop-input" value={value ?? ''} placeholder="Empty" disabled={isDisabled}
      onChange={e => handleChange(e.target.value === '' ? null : Number(e.target.value))} />;
  }
  const format = propDef.format;
  const inputType = format === 'date' ? 'date' : format === 'date-time' ? 'datetime-local' : format === 'time' ? 'time' : format === 'email' ? 'email' : format === 'uri' ? 'url' : 'text';
  if (inputType === 'datetime-local') {
    return <input type="datetime-local" className="prop-input" value={utcIsoToLocalInputValue(value)} disabled={isDisabled}
      onChange={e => handleChange(localInputValueToUtcIso(e.target.value))} />;
  }
  return <input type={inputType} className="prop-input" value={value || ''} placeholder="Empty" disabled={isDisabled}
    onChange={e => handleChange(e.target.value)} />;
}

export default function SchemaForm({ schema, values = {}, onChange, mode = 'view', schemaName, className = '', encryptedFields = [], encKeys = null, layout = 'form', systemFields = [], systemSlot = null }) {
  if (!schema?.properties) return null;
  const requiredFields = schema.required || [];
  const isDisabled = mode === 'preview';
  const encryptedSet = new Set(encryptedFields);
  const entries = Object.entries(schema.properties);

  if (entries.length === 0) return null;

  // Notion-style compact property list (used in the node detail panel). The
  // "content"/"body" field is pulled out into a larger area below the props.
  if (layout === 'properties') {
    const isBodyField = (k) => ['content', 'body'].includes(k.toLowerCase());
    const propEntries = entries.filter(([k]) => !isBodyField(k));
    const bodyEntries = entries.filter(([k]) => isBodyField(k));
    return (
      <div className={`schema-props ${className}`}>
        <div className="schema-props__list">
          {(systemFields.length > 0 || systemSlot) && (
            <div className="schema-props__system">
              {systemFields.map((f) => <SystemPropRow key={f.label} field={f} />)}
              {systemSlot}
            </div>
          )}
          {propEntries.map(([key, propDef]) => {
            const label = propDef.title || formatKey(key);
            const value = values[key];
            // Prefix-driven: the value itself marks ciphertext, so fields
            // revoked from encrypted_fields in a newer schema version stay
            // masked/decryptable instead of rendering raw ciphertext.
            const fieldEncrypted = encryptedSet.has(key) || isEncrypted(value);
            const isRequired = requiredFields.includes(key);
            return (
              <div key={key} className="prop-row">
                <div className="prop-key" title={propDef.description || label}>
                  <FieldIcon propDef={propDef} encrypted={fieldEncrypted} />
                  <span className="prop-key__name">{label}{isRequired && <span className="prop-key__req"> *</span>}</span>
                </div>
                <div className="prop-value">
                  {renderPropValue(key, propDef, value, fieldEncrypted, (v) => onChange?.(key, v), isDisabled, encKeys)}
                </div>
              </div>
            );
          })}
        </div>
        {bodyEntries.map(([key]) => {
          const value = values[key];
          // Ciphertext in the body field (e.g. diary content) must be masked
          // with reveal-to-decrypt, never rendered raw in an editable textarea.
          if (isEncrypted(value)) {
            return (
              <div key={key} className="schema-body">
                <EncryptedViewField value={value} fieldName={key} encKeys={encKeys} />
              </div>
            );
          }
          const isObj = typeof value === 'object' && value !== null;
          const strVal = isObj ? JSON.stringify(value, null, 2) : (value ?? '');
          return (
            <div key={key} className="schema-body">
              <textarea
                className="schema-body__input"
                value={strVal}
                disabled={isDisabled}
                placeholder="Empty"
                rows={4}
                onChange={e => {
                  if (isObj) { try { onChange?.(key, JSON.parse(e.target.value)); } catch { onChange?.(key, e.target.value); } }
                  else onChange?.(key, e.target.value);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`schema-form ${className}`}>
      {schemaName && (
        <div className="schema-form__header">{schemaName}</div>
      )}
      {entries.map(([key, propDef]) => {
        const label = propDef.title || formatKey(key);
        const help = propDef.description || undefined;
        const isRequired = requiredFields.includes(key);
        const isOptional = !isRequired;
        const value = values[key];
        // Prefix-driven: legacy ciphertext stays masked even when the current
        // schema version no longer declares the field encrypted.
        const fieldEncrypted = encryptedSet.has(key) || isEncrypted(value);

        // View mode
        if (mode === 'view') {
          return (
            <FormGroup key={key} label={label} helpText={help} required={isRequired}>
              <div className="schema-form__value">
                {fieldEncrypted
                  ? <EncryptedViewField value={value} fieldName={key} encKeys={encKeys} />
                  : renderViewValue(value)
                }
              </div>
            </FormGroup>
          );
        }

        // Edit / Preview mode
        const handleChange = (newVal) => onChange?.(key, newVal);

        // Encrypted field in edit mode — always password input regardless of schema type
        if (fieldEncrypted) {
          return (
            <Input key={key} label={<><span className="schema-form__encrypted-icon">🔒</span> {label}</>}
              helpText={help} required={isRequired} optional={isOptional}
              type="password"
              value={value || ''} onChange={e => handleChange(e.target.value)}
              disabled={isDisabled}
              autoComplete="new-password"
            />
          );
        }

        // Enum → Select
        if (propDef.enum) {
          return (
            <Select key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
              value={value || ''} onChange={e => handleChange(e.target.value)}
              placeholder="Select..."
              options={propDef.enum.map(v => ({ value: v, label: v }))}
              disabled={isDisabled}
            />
          );
        }

        // Boolean → Toggle
        if (propDef.type === 'boolean') {
          return (
            <FormGroup key={key} label={label} helpText={help} required={isRequired} optional={isOptional}>
              <Toggle checked={!!value} onChange={e => handleChange(e.target.checked)} disabled={isDisabled} />
            </FormGroup>
          );
        }

        // Array → comma-separated input (with object support)
        if (propDef.type === 'array') {
          const items = Array.isArray(value) ? value : [];
          const hasObjects = items.some(item => typeof item === 'object' && item !== null);
          const displayValue = hasObjects
            ? [...new Set(items.map(item => item.username || item.name || item.label || item.title || item.user_id || JSON.stringify(item)))].join(', ')
            : items.join(', ');
          return (
            <FormGroup key={key} label={label} helpText={help} required={isRequired} optional={isOptional}>
              <input
                className="schema-form__array-input"
                disabled={isDisabled || hasObjects}
                value={displayValue}
                onChange={hasObjects ? undefined : (e => handleChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean)))}
                placeholder={isDisabled ? 'value1, value2, ...' : 'Type values separated by commas'}
              />
            </FormGroup>
          );
        }

        // Object → JSON textarea
        if (propDef.type === 'object') {
          const strVal = typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : (value || '');
          return (
            <Textarea key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
              value={strVal} rows={4} disabled={isDisabled}
              onChange={e => {
                try { handleChange(JSON.parse(e.target.value)); } catch { handleChange(e.target.value); }
              }}
              placeholder={isDisabled ? '{ "key": "value" }' : ''}
              style={{ fontFamily: '"Fira Code", "SF Mono", monospace', fontSize: 12 }}
            />
          );
        }

        // Number / Integer
        if (propDef.type === 'number' || propDef.type === 'integer') {
          const step = propDef.multipleOf || (propDef.type === 'integer' ? '1' : 'any');
          return (
            <Input key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
              type="number" step={step}
              min={propDef.minimum} max={propDef.maximum}
              value={value ?? ''} onChange={e => handleChange(e.target.value === '' ? null : Number(e.target.value))}
              disabled={isDisabled}
            />
          );
        }

        // String — determine input type from format
        const format = propDef.format;
        const inputType = format === 'date' ? 'date'
          : format === 'date-time' ? 'datetime-local'
          : format === 'time' ? 'time'
          : format === 'email' ? 'email'
          : format === 'uri' ? 'url'
          : 'text';

        // Long text → textarea (inferred from key name or maxLength)
        if (!format && isLongText(key, propDef)) {
          return (
            <Textarea key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
              value={value || ''} onChange={e => handleChange(e.target.value)}
              disabled={isDisabled}
              minLength={propDef.minLength} maxLength={propDef.maxLength}
            />
          );
        }

        // datetime-local needs UTC↔local translation — widget is tz-naive,
        // storage is canonical UTC ISO (with `Z`).
        if (inputType === 'datetime-local') {
          return (
            <Input key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
              type={inputType}
              value={utcIsoToLocalInputValue(value)}
              onChange={e => handleChange(localInputValueToUtcIso(e.target.value))}
              disabled={isDisabled}
            />
          );
        }

        // Standard string input
        return (
          <Input key={key} label={label} helpText={help} required={isRequired} optional={isOptional}
            type={inputType}
            value={value || ''} onChange={e => handleChange(e.target.value)}
            disabled={isDisabled}
            minLength={propDef.minLength} maxLength={propDef.maxLength}
            pattern={propDef.pattern}
          />
        );
      })}
    </div>
  );
}
